import { parse } from "tldts";
import { CustomerGroup, Policy } from "../models/policy";
import { Decision, OrgEntry } from "../models/decision";

/**
 * Lower-case, trim, strip trailing dot and convert Unicode domains to their ASCII/punycode form (FR-03).
 */
export function normalizeDomain(domain: string): string {
  const trimmed = domain.trim().toLowerCase().replace(/\.$/, "");
  if (trimmed.length === 0) {
    return trimmed;
  }
  try {
    // routes Unicode hostnames through the URL parser's IDNA/punycode conversion
    return new URL(`http://${trimmed}`).hostname;
  } catch {
    return trimmed;
  }
}

/**
 * Extracts and normalizes the domain from an email address. Returns null when the address
 * has no valid "@domain" part (FR-04), so the recipient can be skipped.
 */
export function domainFromAddress(address: string | undefined | null): string | null {
  if (!address) {
    return null;
  }
  const at = address.lastIndexOf("@");
  if (at < 0 || at === address.length - 1) {
    return null;
  }
  const domain = normalizeDomain(address.slice(at + 1));
  return domain.length > 0 ? domain : null;
}

/**
 * True when `domain` equals an entry in `list`, or is a subdomain of one (FR-05/FR-06/FR-07).
 */
export function matchesSuffix(domain: string, list: string[]): boolean {
  return list.some((entry) => {
    const normalizedEntry = normalizeDomain(entry);
    return domain === normalizedEntry || domain.endsWith(`.${normalizedEntry}`);
  });
}

export function customerGroupFor(domain: string, policy: Policy) {
  return policy.customerGroups.find((group) => matchesSuffix(domain, group.domains));
}

/**
 * Reduces any other external domain to its registrable domain using the Public Suffix List (FR-08),
 * e.g. mail.supplier.com.mx -> supplier.com.mx.
 */
export function registrableDomain(domain: string): string {
  const parsed = parse(domain);
  return parsed.domain ?? domain;
}

/**
 * Returns the first label name (the label itself or a parent) that the policy blocks, or undefined.
 */
export function blockedLabelMatch(labelNames: string[], policy: Policy): string | undefined {
  if (!policy.labelRule.enabled) {
    return undefined;
  }
  const blocked = policy.labelRule.blockedLabelNames.map((n) => n.trim().toLowerCase());
  return labelNames.find((name) => blocked.includes(name.trim().toLowerCase()));
}

/**
 * The customer whose own confidential label (CustomerGroup.labelNames) is applied, with the label's name.
 */
export function ownerGroupForLabels(
  labelNames: string[],
  groups: CustomerGroup[]
): { group: CustomerGroup; labelName: string } | undefined {
  for (const group of groups) {
    const own = (group.labelNames ?? []).map((n) => n.trim().toLowerCase());
    const labelName = labelNames.find((n) => own.includes(n.trim().toLowerCase()));
    if (labelName) {
      return { group, labelName };
    }
  }
  return undefined;
}

export interface MessageContext {
  /** subject, attachment names and the start of the body */
  text: string;
  /** To and CC addresses (BCC excluded) */
  toCcAddresses: Array<string | undefined | null>;
}

const MIN_LOOKALIKE_LENGTH = 8;
const MIN_BRAND_LENGTH = 5;

const alphanumericOnly = (text: string): string => text.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The customer whose name (or a keyword of 5+ letters) appears inside `domain`, ignoring dots and hyphens,
 * e.g. honda-parts.com -> Honda. Callers only pass domains that aren't any customer's known domain.
 */
export function brandInDomain(domain: string, groups: CustomerGroup[]): CustomerGroup | undefined {
  const host = alphanumericOnly(domain);
  return groups.find((group) =>
    [group.name, ...(group.keywords ?? [])]
      .map(alphanumericOnly)
      .some((term) => term.length >= MIN_BRAND_LENGTH && host.includes(term))
  );
}

export function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = row[0] ?? 0;
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = row[j] ?? 0;
      row[j] = Math.min(above + 1, (row[j - 1] ?? 0) + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return row[b.length] ?? 0;
}

/**
 * Returns the known domain that `domain` almost matches (one typo, or only hyphens differ), e.g.
 * hond.com -> honda.com. Short domains are skipped to avoid noise.
 */
export function findLookalike(domain: string, knownDomains: string[]): string | undefined {
  const candidate = registrableDomain(domain);
  for (const known of knownDomains) {
    const knownRegistrable = registrableDomain(normalizeDomain(known));
    if (candidate === knownRegistrable) {
      continue;
    }
    const a = candidate.replace(/-/g, "");
    const b = knownRegistrable.replace(/-/g, "");
    if (Math.min(a.length, b.length) < MIN_LOOKALIKE_LENGTH) {
      continue;
    }
    if (a === b || editDistance(a, b) === 1) {
      return knownRegistrable;
    }
  }
  return undefined;
}

const isAlphanumeric = (ch: string | undefined): boolean => ch !== undefined && /[a-z0-9]/.test(ch);

/**
 * Case-insensitive whole-word search; a keyword's edge only needs a word boundary when it is a letter or digit.
 */
export function containsKeyword(text: string, keyword: string): boolean {
  const haystack = text.toLowerCase();
  const needle = keyword.trim().toLowerCase();
  if (needle.length === 0) {
    return false;
  }
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) {
      return false;
    }
    const startOk = !isAlphanumeric(needle[0]) || !isAlphanumeric(haystack[at - 1]);
    const endOk = !isAlphanumeric(needle[needle.length - 1]) || !isAlphanumeric(haystack[at + needle.length]);
    if (startOk && endOk) {
      return true;
    }
    from = at + 1;
  }
}

export function mentionsCustomer(text: string, group: CustomerGroup): boolean {
  return [group.name, ...(group.keywords ?? [])].some((keyword) => containsKeyword(text, keyword));
}

/**
 * Number of distinct external addresses (internal and ignored domains excluded).
 */
export function countExternalAddresses(addresses: Array<string | undefined | null>, policy: Policy): number {
  const external = new Set<string>();
  for (const address of addresses) {
    const domain = domainFromAddress(address);
    if (!domain || matchesSuffix(domain, policy.internalDomains) || matchesSuffix(domain, policy.ignoredDomains)) {
      continue;
    }
    external.add((address as string).trim().toLowerCase());
  }
  return external.size;
}

/**
 * `labelNames` is the sensitivity label's name followed by its parent names; [] means the message has
 * no label, and null means the label couldn't be determined (never blocks, fail open).
 * `ctx` carries the message content and To/CC list; without it the content and recipient-count rules are skipped.
 */
export function evaluate(
  addresses: Array<string | undefined | null>,
  policy: Policy,
  labelNames: string[] | null = null,
  ctx?: MessageContext
): Decision {
  const orgs = new Map<string, OrgEntry>();
  const lookalikes = new Map<string, string>();
  const brandDomains = new Map<string, string>();
  const knownDomains = [...policy.internalDomains, ...policy.customerGroups.flatMap((g) => g.domains)];

  for (const address of addresses) {
    const domain = domainFromAddress(address);
    if (!domain) {
      continue; // FR-04: unresolved / legacy entries are skipped
    }
    if (matchesSuffix(domain, policy.internalDomains)) {
      continue; // FR-05
    }
    if (matchesSuffix(domain, policy.ignoredDomains)) {
      continue; // FR-06
    }

    const group = customerGroupFor(domain, policy);
    if (group) {
      orgs.set(group.id, { id: group.id, label: group.name, isCustomer: true }); // FR-07
    } else {
      const rd = registrableDomain(domain); // FR-08
      orgs.set(rd, { id: rd, label: rd, isCustomer: false });
      const similarTo = policy.lookalikeRule.enabled ? findLookalike(domain, knownDomains) : undefined;
      if (similarTo) {
        lookalikes.set(rd, similarTo);
      } else if (policy.brandDomainRule.enabled) {
        const brand = brandInDomain(rd, policy.customerGroups);
        if (brand) {
          brandDomains.set(rd, brand.name);
        }
      }
    }
  }

  const all = Array.from(orgs.values());
  const customers = all.filter((o) => o.isCustomer);
  const others = all.filter((o) => !o.isCustomer);
  const orgLabels = [...customers, ...others].map((o) => o.label);

  const labelName = all.length > 0 && labelNames ? blockedLabelMatch(labelNames, policy) : undefined;
  if (labelName) {
    return { level: "blockedLabel", orgCount: all.length, customerCount: customers.length, orgLabels, labelName };
  }
  if (all.length > 0 && labelNames?.length === 0 && policy.labelRule.enabled && policy.labelRule.blockUnlabeled) {
    return { level: "blockedUnlabeled", orgCount: all.length, customerCount: customers.length, orgLabels };
  }
  const owner =
    all.length > 0 && labelNames && policy.customerLabelRule.enabled
      ? ownerGroupForLabels(labelNames, policy.customerGroups)
      : undefined;
  if (owner) {
    const offending = all.filter(
      (o) => o.id !== owner.group.id && (policy.customerLabelRule.scope === "anyOtherExternal" || o.isCustomer)
    );
    if (offending.length > 0) {
      const detail = offending
        .map((o) => o.label)
        .slice(0, 3)
        .join(", ");
      return {
        level: "blockedCustomerLabel",
        orgCount: all.length,
        customerCount: customers.length,
        orgLabels,
        labelName: owner.labelName,
        detail,
      };
    }
  }
  const base = { orgCount: all.length, customerCount: customers.length, orgLabels };

  if (lookalikes.size > 0) {
    const detail = Array.from(lookalikes, ([domain, similarTo]) => `${domain} \u2248 ${similarTo}`)
      .slice(0, 3)
      .join(", ");
    return { level: "lookalikeDomain", ...base, detail };
  }
  if (brandDomains.size > 0) {
    const detail = Array.from(brandDomains, ([domain, name]) => `${domain} (${name})`)
      .slice(0, 3)
      .join(", ");
    return { level: "brandInDomain", ...base, detail };
  }
  if (customers.length >= 2) {
    return { level: "crossCustomer", orgCount: all.length, customerCount: customers.length, orgLabels }; // FR-10
  }
  if (ctx && policy.contentRule.enabled && customers.length > 0) {
    const recipientGroupIds = new Set(customers.map((c) => c.id));
    const mentioned = policy.customerGroups.filter(
      (g) => !recipientGroupIds.has(g.id) && mentionsCustomer(ctx.text, g)
    );
    if (mentioned.length > 0) {
      return { level: "contentMismatch", ...base, detail: mentioned.map((g) => g.name).join(", ") };
    }
  }
  if (ctx && policy.externalLimitRule.enabled) {
    const external = countExternalAddresses(ctx.toCcAddresses, policy);
    if (external > policy.externalLimitRule.max) {
      return { level: "tooManyExternal", ...base, detail: String(external) };
    }
  }
  if (all.length >= policy.threshold) {
    return { level: "standard", orgCount: all.length, customerCount: customers.length, orgLabels }; // FR-09
  }
  return { level: "none", orgCount: all.length, customerCount: customers.length, orgLabels }; // FR-11
}
